import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { supabase } from '../../lib/supabaseClient';

export default function SignupPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    smsNotifications: false,
    emailNotifications: true,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate('/dashboard');
      }
    };
    checkUser();
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    if (formData.phone && formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Sign up the user
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
          },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        setIsLoading(false);
        return;
      }

      if (data.user) {
        // Insert user profile data
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            id: data.user.id,
            email: formData.email,
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
            sms_notifications: formData.smsNotifications,
            email_notifications: formData.emailNotifications,
          });

        if (profileError) {
          console.error('Error creating user profile:', profileError);
        }

        // Check if there's a checkout return state
        const checkoutReturn = localStorage.getItem('checkout_return');
        if (checkoutReturn) {
          localStorage.removeItem('checkout_return');
          navigate('/checkout');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-sand">
      <Navigation />
      
      <main className="flex-grow pt-24 pb-16 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl font-light text-charcoal mb-4 tracking-wide">
              Create Account
            </h1>
            <p className="text-base font-light text-greige">
              Join us to manage your memberships and track your wellness journey
            </p>
          </div>

          <div className="bg-bone rounded-2xl p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Personal Information */}
              <div>
                <h2 className="text-xl font-light text-charcoal mb-4 tracking-wide">
                  Personal Information
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h2 className="text-xl font-light text-charcoal mb-4 tracking-wide">
                  Contact Information
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">Phone Number (Optional)</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                      placeholder="1234567890"
                    />
                  </div>
                </div>
              </div>

              {/* Password */}
              <div>
                <h2 className="text-xl font-light text-charcoal mb-4 tracking-wide">
                  Create Password
                </h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">Password *</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                      placeholder="At least 6 characters"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-light text-greige mb-2">Confirm Password *</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-greige/20 rounded-lg text-charcoal font-light focus:outline-none focus:border-gold transition-colors"
                      placeholder="Re-enter your password"
                    />
                  </div>
                </div>
              </div>

              {/* Notification Preferences */}
              <div>
                <h2 className="text-xl font-light text-charcoal mb-4 tracking-wide">
                  Notification Preferences
                </h2>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="emailNotifications"
                      checked={formData.emailNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-gold border-greige/20 rounded focus:ring-gold cursor-pointer"
                    />
                    <span className="text-sm font-light text-charcoal">
                      Receive email notifications about appointments and updates
                    </span>
                  </label>
                  
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="smsNotifications"
                      checked={formData.smsNotifications}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-gold border-greige/20 rounded focus:ring-gold cursor-pointer"
                    />
                    <span className="text-sm font-light text-charcoal">
                      Receive SMS text notifications about appointments
                    </span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="text-sm text-red-600 font-light flex items-center gap-2">
                    <i className="ri-error-warning-line"></i>
                    {error}
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-4 bg-charcoal text-white text-base font-light tracking-wide rounded-full hover:bg-charcoal/80 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Creating Account...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div className="mt-6 pt-6 border-t border-greige/10 text-center">
              <p className="text-sm font-light text-greige">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-gold hover:text-gold/80 transition-colors cursor-pointer"
                >
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}